import React from 'react';
import { PaintSettings, PaintFinishType, PAINT_PRESETS } from '../../types/vehicle';
import { Sparkles, Shield, Palette, Check } from 'lucide-react';

export interface PaintRackProps {
  paint: PaintSettings;
  onChangePaint: (updates: Partial<PaintSettings>) => void;
}

export const PaintRack: React.FC<PaintRackProps> = ({ paint, onChangePaint }) => {
  const finishTypes: {
    type: PaintFinishType;
    label: string;
    icon: React.ReactNode;
    desc: string;
  }[] = [
    {
      type: 'acrylic_lacquer',
      label: 'Acrylic Lacquer',
      icon: <Sparkles className="w-4 h-4 text-safety-amber" />,
      desc: 'High-gloss mirror clearcoat with crisp overhead shop light reflections.',
    },
    {
      type: 'satin_primer',
      label: 'Satin Primer',
      icon: <Shield className="w-4 h-4 text-shop-muted" />,
      desc: 'Low-sheen hot rod epoxy primer with diffuse soft matte falloff.',
    },
    {
      type: 'metallic_enamel',
      label: 'Metallic Enamel',
      icon: <Palette className="w-4 h-4 text-safety-cyan" />,
      desc: 'Deep metalflake aluminum flecks with high-pigment clearcoat hotspot.',
    },
  ];

  return (
    <div className="space-y-6">
      {/* 1. Finish Technology Selector */}
      <div className="space-y-2">
        <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-shop-muted">
          1. Coating Technology &amp; Physics Model
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {finishTypes.map((item) => {
            const isSelected = paint.finishType === item.type;
            return (
              <button
                key={item.type}
                onClick={() => {
                  let defaultGloss = paint.clearcoatGloss;
                  let defaultFleck = paint.metallicFleckDensity;
                  if (item.type === 'acrylic_lacquer') {
                    defaultGloss = 0.95;
                    defaultFleck = 0.0;
                  } else if (item.type === 'satin_primer') {
                    defaultGloss = 0.15;
                    defaultFleck = 0.0;
                  } else if (item.type === 'metallic_enamel') {
                    defaultGloss = 0.90;
                    defaultFleck = 0.65;
                  }
                  onChangePaint({
                    finishType: item.type,
                    clearcoatGloss: defaultGloss,
                    metallicFleckDensity: defaultFleck,
                  });
                }}
                className={`min-h-[56px] p-3 rounded border text-left flex items-start gap-2.5 transition-all duration-100 cursor-pointer ${
                  isSelected
                    ? 'bg-shop-panel text-shop-highlight border-safety-amber shadow-[inset_0_2px_0_0_#f59e0b]'
                    : 'bg-shop-dark text-shop-muted border-shop-border hover:bg-shop-well hover:text-shop-light'
                }`}
              >
                <div className="mt-0.5 shrink-0">{item.icon}</div>
                <div className="min-w-0">
                  <span className="text-xs font-bold uppercase tracking-wider block text-shop-highlight">
                    {item.label}
                  </span>
                  <p className="text-[10px] text-shop-muted mt-0.5 line-clamp-2">
                    {item.desc}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. 18 Period Color Presets */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-shop-muted">
            2. 18 Period-Correct Vintage Paint Presets
          </h4>
          <span className="text-[11px] font-mono text-safety-amber font-bold">
            {paint.presetName || 'Custom Formulation'}
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
          {PAINT_PRESETS.map((preset) => {
            const isSelected =
              paint.colorHex.toLowerCase() === preset.colorHex.toLowerCase() &&
              paint.presetName === preset.name;
            return (
              <button
                key={preset.id}
                onClick={() =>
                  onChangePaint({
                    colorHex: preset.colorHex,
                    presetName: preset.name,
                    finishType: preset.finishType,
                    clearcoatGloss: preset.clearcoatGloss,
                    metallicFleckDensity: preset.metallicFleckDensity,
                  })
                }
                className={`min-h-[58px] p-2 rounded border text-left flex flex-col justify-between transition-all duration-100 cursor-pointer ${
                  isSelected
                    ? 'bg-shop-panel border-safety-amber shadow-[0_0_0_2px_#f59e0b]'
                    : 'bg-shop-dark border-shop-border hover:bg-shop-well'
                }`}
                title={`${preset.name} (${preset.eraDescription})`}
              >
                {/* Color Swatch Bar */}
                <div className="flex items-center justify-between w-full mb-1">
                  <div
                    className="w-full h-5 rounded border border-[#0d1117] flex items-center justify-end px-1 shadow-sm"
                    style={{ backgroundColor: preset.colorHex }}
                  >
                    {isSelected && (
                      <span className="w-3.5 h-3.5 rounded-full bg-shop-darkest flex items-center justify-center text-safety-amber">
                        <Check className="w-2.5 h-2.5" />
                      </span>
                    )}
                  </div>
                </div>

                {/* Swatch Label */}
                <div className="min-w-0">
                  <div className="text-[11px] font-bold text-shop-highlight truncate">
                    {preset.name}
                  </div>
                  <div className="text-[9px] font-mono text-shop-muted truncate">
                    {preset.colorHex.toUpperCase()}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 3. Custom Color Formulation & Shading Controls */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-shop-darkest border border-shop-border rounded">
        {/* Custom Hex / Color Input */}
        <div className="space-y-1.5">
          <label className="text-[11px] font-mono uppercase tracking-wider text-shop-muted block">
            Custom Hex Color
          </label>
          <div className="flex items-center gap-2">
            <div className="relative w-11 h-11 shrink-0 rounded border border-shop-border overflow-hidden bg-shop-panel">
              <input
                type="color"
                value={paint.colorHex}
                onChange={(e) =>
                  onChangePaint({
                    colorHex: e.target.value,
                    presetName: 'Custom Mix',
                  })
                }
                className="absolute -top-2 -left-2 w-16 h-16 cursor-pointer border-0 p-0"
              />
            </div>
            <input
              type="text"
              value={paint.colorHex}
              onChange={(e) =>
                onChangePaint({
                  colorHex: e.target.value,
                  presetName: 'Custom Mix',
                })
              }
              className="flex-1 min-h-[44px] px-3 bg-shop-panel text-shop-highlight border border-shop-border rounded font-mono text-xs uppercase"
              placeholder="#EA580C"
            />
          </div>
        </div>

        {/* Clearcoat Gloss Slider */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-[11px] font-mono text-shop-muted">
            <span>CLEARCOAT GLOSS</span>
            <span className="font-bold text-shop-highlight">
              {Math.round(paint.clearcoatGloss * 100)}%
            </span>
          </div>
          <div className="h-11 flex items-center">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={paint.clearcoatGloss}
              onChange={(e) =>
                onChangePaint({
                  clearcoatGloss: parseFloat(e.target.value),
                })
              }
              className="w-full accent-safety-amber h-2 bg-shop-panel rounded cursor-pointer"
            />
          </div>
        </div>

        {/* Metallic Fleck Density Slider */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-[11px] font-mono text-shop-muted">
            <span>METALLIC FLECK DENSITY</span>
            <span className="font-bold text-shop-highlight">
              {Math.round(paint.metallicFleckDensity * 100)}%
            </span>
          </div>
          <div className="h-11 flex items-center">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={paint.metallicFleckDensity}
              disabled={paint.finishType !== 'metallic_enamel'}
              onChange={(e) =>
                onChangePaint({
                  metallicFleckDensity: parseFloat(e.target.value),
                })
              }
              className="w-full accent-safety-amber h-2 bg-shop-panel rounded cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
